import { createElement, useMemo, useSyncExternalStore } from "react";
import { createToastRailGesture } from "./toastRailGesture";

const RECORDING_TOAST_PREFIX = "recording-progress-";
const EMPTY_RECORDING_TOAST = Object.freeze({ message: "", phase: "working", progress: null });
const recordingToastStates = new Map();
const recordingToastListeners = new Map();
const recordingToastDismissTimers = new Map();
const recordingToastCleanupTimers = new Map();
const recordingToastProgress = new Map();
const recordingToastAliases = new Map();
// Per-capsule "this stalled" handler, kept in module state rather than passed on
// every call: the ceiling timer is RE-ARMED by each showRecordingProgressToast, and
// most of those calls (a progress tick from the shell) have no business knowing who
// owns the recording. Registered once by the caller that took ownership.
const recordingToastCeilingHandlers = new Map();

// The shell reports progress as TWO sequential sweeps on ONE capsule: it
// transcodes the file to MP3, then uploads it, and each climbs 0 -> 0.95.
// (0.95, not 1: lucasai-app hooks/usePostMessages.js:168 caps it so the bar
// never reads "done" before the transfer is acknowledged. Completion arrives as
// doneToast, not as a progress value.) Rendered raw, a clinician would watch the
// number climb, snap back to zero, and climb again.
//
// These bounds fold the two sweeps into one bar that only ever moves forward.
// Upload gets the larger share because it is the part that actually takes time
// on a weak connection -- transcoding is local CPU on a file that already exists.
const PROGRESS_STAGE_BOUNDS = [
  [0, 0.35],
  [0.35, 1],
];

/**
 * Folds the shell's per-stage progress onto a single monotonic 0..1 scale.
 *
 * The stage boundary is detected STRUCTURALLY -- a value below the previous one
 * is the shell starting its next sweep -- so this never has to read the shell's
 * wording. Returns undefined for a non-numeric value, which is how a capsule
 * stays indeterminate until real progress arrives.
 */
export function foldRecordingProgress(toastId, rawProgress) {
  if (!Number.isFinite(rawProgress)) return undefined;

  // Clamped but NOT normalised against the cap. Dividing by 0.95 to "use the
  // full bar" would show 100% the moment the last byte left the device, which is
  // the stuck-at-100% state the shell caps its own value to avoid. Carrying the
  // cap through means the bar tops out just short and the checkmark -- driven by
  // doneToast, the real acknowledgement -- is what completes it.
  const clamped = Math.min(Math.max(rawProgress, 0), 1);
  const tracked = recordingToastProgress.get(toastId) || { stage: 0, lastRaw: 0 };

  if (clamped < tracked.lastRaw) tracked.stage = Math.min(tracked.stage + 1, PROGRESS_STAGE_BOUNDS.length - 1);
  tracked.lastRaw = clamped;
  recordingToastProgress.set(toastId, tracked);

  const [from, to] = PROGRESS_STAGE_BOUNDS[tracked.stage];
  return from + clamped * (to - from);
}

// Last-resort ceiling for a capsule still in its working phase. The capsule is
// sticky by design (autoClose false, no close button) because it spans upload →
// transcription, so it needs SOME clock or a lifecycle that never reaches a
// terminal event strands a spinner on screen forever. Deliberately above
// useTranscriptionWatchdog's LONG_RECORDING_CEILING_MS (480s) so the watchdog's
// honest-fail toast always speaks first on the paths it covers; this only fires
// for the ones it cannot see, e.g. a native upload that aborts before the
// watchdog is ever armed. Re-armed on every state change, so it measures
// silence, not total lifetime.
export const RECORDING_TOAST_WORKING_CEILING_MS = 600000;

export const recordingProgressToastId = (recordingId) => `${RECORDING_TOAST_PREFIX}${recordingId}`;

// How many id pairs are remembered at once. Recordings are serialised, so this
// only has to outlive the handful whose capsules are still on screen.
export const RECORDING_TOAST_ALIAS_LIMIT = 32;

/**
 * Points one recording id at another recording's capsule.
 *
 * ONE recording carries TWO names. The native shell names it by its own device
 * row id and never learns ours (it stores the recordingUuid we send as a
 * separate `uuid` field), while the backend names it by that recordingUuid --
 * the id we hand to Transcript - Save and the one it echoes back on
 * transcriptionCompleted. The capsule is necessarily opened under the SHELL's
 * name, because the shell's transcode/upload toast arrives long before the
 * backend has anything to say.
 *
 * So every completion signal we own -- ready, dismiss-on-failure, the watchdog's
 * stall retraction (useChats.js) -- addressed a capsule id that had never been
 * mounted: the failure paths dismissed nothing and the success path OPENED A
 * SECOND capsule while the first spun on to its 600s ceiling, with the record
 * button locked behind the same unfinished transcription the whole time.
 *
 * Callers link the pair at the two bridge events that carry both names
 * (syncRecording and audioRecorded). Resolution is a single hop and a self-link
 * is dropped, so old app builds -- which send no recordingUuid, making both ids
 * the shell's rowid -- are unaffected.
 */
export function linkRecordingProgressToast(aliasRecordingId, canonicalRecordingId) {
  if (!aliasRecordingId || !canonicalRecordingId) return;
  if (String(aliasRecordingId) === String(canonicalRecordingId)) return;
  recordingToastAliases.set(recordingProgressToastId(aliasRecordingId), recordingProgressToastId(canonicalRecordingId));

  // Most links are swept when their capsule is cleaned up, but not all of them
  // get a capsule to be swept with: a silent Quick Start recording retracts its
  // capsule BEFORE the handoff that registers the link. This module lives for
  // the whole webview session -- days and hundreds of recordings on the native
  // app -- so the oldest link goes when the map is full. Recordings are
  // serialised, so anything still in flight is at the young end.
  //
  // LIVE links are exempt. Evicting the alias of a capsule that is still mounted
  // breaks the only route its completion signal has -- the SSE dismissal, keyed by
  // uuid, resolves to nothing and the capsule survives to its ceiling, where it is
  // now read as a stall: a false "hasn't been saved" toast and a redundant re-offer
  // for a recording that saved fine. Skipping them can exceed the limit, which is
  // the correct trade: the cap exists to bound idle growth, not to evict live work.
  for (const oldest of Array.from(recordingToastAliases.keys())) {
    if (recordingToastAliases.size <= RECORDING_TOAST_ALIAS_LIMIT) break;
    const canonical = recordingToastAliases.get(oldest);
    if (recordingToastStates.has(canonical) || recordingToastDismissTimers.has(canonical)) continue;
    recordingToastAliases.delete(oldest);
  }
}

const resolveRecordingToastId = (toastId) => recordingToastAliases.get(toastId) || toastId;

export const getRecordingIdFromNativeToast = ({ toastId, message } = {}) => {
  if (typeof toastId === "string") {
    if (toastId.startsWith(RECORDING_TOAST_PREFIX)) return toastId.slice(RECORDING_TOAST_PREFIX.length);
    if (toastId.startsWith("transcode-")) return toastId.slice("transcode-".length);
  }

  if (typeof message !== "string") return undefined;

  // An explicit phase word is REQUIRED. An earlier `|$` alternative also matched
  // any message merely ENDING in "recording <token>" — e.g. "Could not save
  // recording rec-1" — and because usePostMessages checks isRecordingToast
  // BEFORE type === "error", a match there discarded the error's severity, its
  // text and its auto-close, turning a real failure into a permanent spinner.
  const match = message.match(/\brecording\s+([^\s.]+)(?:\.\.\.|\s+ready\b|\s+sent\b)/i);
  return match?.[1];
};

function recordingToastSnapshot(toastId) {
  return recordingToastStates.get(toastId) || EMPTY_RECORDING_TOAST;
}

function subscribeToRecordingToast(toastId, listener) {
  let listeners = recordingToastListeners.get(toastId);
  if (!listeners) {
    listeners = new Set();
    recordingToastListeners.set(toastId, listeners);
  }
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) recordingToastListeners.delete(toastId);
  };
}

function setRecordingToastState(toastId, nextState) {
  const cleanupTimer = recordingToastCleanupTimers.get(toastId);
  if (cleanupTimer) {
    clearTimeout(cleanupTimer);
    recordingToastCleanupTimers.delete(toastId);
  }

  const current = recordingToastSnapshot(toastId);
  if (current.message === nextState.message && current.phase === nextState.phase && current.progress === nextState.progress) {
    return;
  }

  recordingToastStates.set(toastId, Object.freeze({ ...current, ...nextState }));
  recordingToastListeners.get(toastId)?.forEach((listener) => listener());
}

function cleanupRecordingToast(toastId) {
  const timer = recordingToastDismissTimers.get(toastId);
  if (timer) clearTimeout(timer);
  recordingToastDismissTimers.delete(toastId);
  recordingToastProgress.delete(toastId);
  recordingToastCeilingHandlers.delete(toastId);
  // The capsule is finished, so its alias has nothing left to point at. Dropping
  // it here bounds the map: this module lives for the whole webview session,
  // which on the native app can be days and hundreds of recordings.
  recordingToastAliases.forEach((canonical, alias) => {
    if (canonical === toastId) recordingToastAliases.delete(alias);
  });

  // Hold the final frame until the capsule is really gone, keyed on whether it
  // is still MOUNTED rather than on a guessed duration.
  //
  // This used to fire once at 500ms and notify listeners. React-Toastify's exit
  // is a 500ms animation followed by a 300ms height collapse, so that landed
  // squarely mid-exit and re-rendered the capsule from the empty snapshot: the
  // message blanked and `phase` reverted from "ready" to "working", swapping the
  // checkmark back to a spinner over a 180ms transition while the toast was
  // still on screen. That was the flicker on dismiss.
  //
  // A live listener set means the component is still mounted, so wait. No
  // notification is needed on the way out -- by the time the state is dropped
  // nothing is subscribed to hear about it.
  let attempts = 0;
  const sweep = () => {
    const listeners = recordingToastListeners.get(toastId);
    if (listeners?.size && attempts++ < 40) {
      recordingToastCleanupTimers.set(toastId, setTimeout(sweep, 250));
      return;
    }
    recordingToastCleanupTimers.delete(toastId);
    recordingToastStates.delete(toastId);
  };
  recordingToastCleanupTimers.set(toastId, setTimeout(sweep, 250));
}

export function RecordingProgressToast({ toastId, closeToast }) {
  const snapshot = useSyncExternalStore(
    (listener) => subscribeToRecordingToast(toastId, listener),
    () => recordingToastSnapshot(toastId),
    () => recordingToastSnapshot(toastId)
  );

  // No `role` / `aria-live` here on purpose. React-Toastify already stamps
  // `role="alert"` on the toast element that wraps this, and the rail's
  // container section is itself `aria-live="polite" aria-relevant="additions
  // text"`. A third nested live region made every phase change announce two or
  // three times. `ToastRailContent` adds none for the same reason.
  // Opting out of ToastRailContent (see toastRailSelfContained below) also opted
  // out of the rail's flick-to-dismiss, leaving React-Toastify's distance drag as
  // the only way to get rid of this capsule -- and that needs 80% of the
  // capsule's width, ~248px, i.e. two thirds of a phone screen. The rail gesture
  // asks for 24px plus a flick, which is what every other rail toast responds to.
  // `closeToast` is injected by v11's cloneElement on a non-string content type.
  const gesture = useMemo(
    () =>
      createToastRailGesture({
        canDismiss: true,
        onDismiss: typeof closeToast === "function" ? () => closeToast(true) : undefined,
      }),
    [closeToast]
  );

  const determinate = Number.isFinite(snapshot.progress) && snapshot.phase !== "ready";
  const percent = determinate ? Math.round(Math.min(Math.max(snapshot.progress, 0), 1) * 100) : null;
  // r=8 in a 20x20 box. Circumference drives the dash so the arc length IS the
  // percentage; the ring is rotated -90deg in CSS so it fills from 12 o'clock.
  const circumference = 2 * Math.PI * 8;

  return createElement(
    "div",
    {
      className: "toast-rail-content recording-progress-toast",
      "data-phase": snapshot.phase,
      "data-determinate": determinate ? "true" : "false",
      ...gesture,
    },
    createElement(
      "span",
      { className: "recording-progress-toast__status", "aria-hidden": "true" },
      createElement("span", { className: "recording-progress-toast__spinner" }),
      createElement(
        "svg",
        { className: "recording-progress-toast__ring", viewBox: "0 0 20 20", focusable: "false" },
        createElement("circle", { className: "recording-progress-toast__ring-track", cx: 10, cy: 10, r: 8 }),
        createElement("circle", {
          className: "recording-progress-toast__ring-value",
          cx: 10,
          cy: 10,
          r: 8,
          style: {
            strokeDasharray: circumference,
            strokeDashoffset: circumference * (1 - (determinate ? Math.min(Math.max(snapshot.progress, 0), 1) : 0)),
          },
        })
      ),
      createElement(
        "svg",
        { className: "recording-progress-toast__check", viewBox: "0 0 20 20", focusable: "false" },
        createElement("path", { d: "m5.25 10.25 3 3 6.5-7" })
      )
    ),
    createElement("span", { className: "toast-rail-content__source recording-progress-toast__message" }, snapshot.message),
    percent === null ? null : createElement("span", { className: "recording-progress-toast__percent" }, `${percent}%`)
  );
}

// The recording capsule already has the rail's content structure. Skipping the
// controller's decorating toast.update prevents React-Toastify from remounting
// it immediately after arrival.
//
// Know what this opts OUT of, because it is the whole rail adapter and not just
// that one update: no `+N` overflow badge when this capsule is the visible one,
// no `xN` repeat count, no rail velocity-swipe, and no `data-expanded` /
// `data-paused` (unstyled today, so no visual effect — but a future rail feature
// keyed on those attributes will silently skip this capsule). React-Toastify's
// own horizontal distance-drag still closes it, which is the one dismissal path
// the capsule keeps.
RecordingProgressToast.toastRailSelfContained = true;

export function showRecordingProgressToast(
  toastApi,
  {
    toastId: requestedToastId,
    message,
    stageMessages,
    phase = "working",
    progress,
    autoDismissMs,
    onWorkingCeiling,
    options = {},
  }
) {
  if (!requestedToastId) return;
  const toastId = resolveRecordingToastId(requestedToastId);

  // Only ever ADDED, never cleared by a later call that omits it — see the map's
  // declaration for why most callers must not have to re-supply it.
  if (onWorkingCeiling) recordingToastCeilingHandlers.set(toastId, onWorkingCeiling);

  // A call that carries no progress leaves whatever the capsule already has, so
  // a message-only update does not drop the bar back to indeterminate. An
  // EXPLICIT null does clear it: that is a caller saying the work is no longer
  // measurable -- server-side transcription reports nothing until it is done, so
  // carrying the upload's last value into that stretch left a dead percentage
  // frozen beside "Preparing your transcription" for the life of the capsule.
  // Reaching `ready` clears it too: the checkmark is the completion signal and a
  // stale percentage beside it would contradict it.
  const current = recordingToastSnapshot(toastId);
  const folded = foldRecordingProgress(toastId, progress);
  const nextProgress = phase === "ready" || progress === null ? null : (folded ?? current.progress ?? null);

  // Stage-accurate wording. The shell transcodes before it uploads, so saying
  // "Uploading" for the whole span would be wrong for the first stretch. The
  // caller supplies one string per stage and this module picks, because this is
  // what tracks the stage. Read AFTER the fold so it reflects THIS update, and
  // skipped for `ready`, whose message is a completion line, not a stage.
  const stage = recordingToastProgress.get(toastId)?.stage ?? 0;
  const staged = phase === "ready" ? undefined : stageMessages?.[stage];

  setRecordingToastState(toastId, { message: staged ?? message, phase, progress: nextProgress });

  // `toast.isActive` is authoritative the instant `toast.info` returns:
  // react-toastify v11's buildToast pushes synchronously and sets isActive
  // there, so there is no create-then-check race and no need for a local
  // mounted-id mirror (an earlier one was written but never read).
  if (!toastApi?.isActive?.(toastId)) {
    toastApi?.info?.(createElement(RecordingProgressToast, { toastId }), {
      ...options,
      toastId,
      autoClose: false,
      closeButton: false,
      icon: false,
    });
  }

  const previousTimer = recordingToastDismissTimers.get(toastId);
  if (previousTimer) clearTimeout(previousTimer);

  // Every capsule gets a timer. An explicit autoDismissMs is the ready phase's
  // short display interval; everything else falls back to the working-phase
  // ceiling so no lifecycle can leave a spinner mounted indefinitely, and so the
  // module-level state for a capsule dismissed some other way still gets swept.
  const hasExplicitDismiss = Number.isFinite(autoDismissMs) && autoDismissMs > 0;
  const dismissAfter = hasExplicitDismiss ? autoDismissMs : RECORDING_TOAST_WORKING_CEILING_MS;
  recordingToastDismissTimers.set(
    toastId,
    setTimeout(() => {
      // Reaching the WORKING ceiling means this capsule went silent for the whole
      // window, measured from whichever call armed THIS timer — callers re-arm at
      // each real milestone, so silence here means the owner stopped reporting, and
      // no terminal signal (which would have cleared this timer) ever arrived.
      //
      // An explicit autoDismissMs is the `ready` phase's display interval — a
      // terminal SUCCESS — so it is never a stall. `phase` is read from the arming
      // call, not from module state: every show re-arms and every dismissal clears,
      // so the firing timer is always the newest one and its phase cannot have
      // drifted underneath it.
      const stalled = !hasExplicitDismiss && phase === "working";
      const onCeiling = stalled ? recordingToastCeilingHandlers.get(toastId) : undefined;
      toastApi?.dismiss?.(toastId);
      cleanupRecordingToast(toastId);
      // After cleanup: the handler hands the recording back to the shell, which can
      // re-offer it immediately, and that next lifecycle must not be torn down by
      // this one's sweep.
      onCeiling?.();
    }, dismissAfter)
  );
}

export function dismissRecordingProgressToast(toastApi, requestedToastId) {
  if (!requestedToastId) return;
  const toastId = resolveRecordingToastId(requestedToastId);
  toastApi?.dismiss?.(toastId);
  cleanupRecordingToast(toastId);
}

export function resetRecordingProgressToastsForTests() {
  recordingToastDismissTimers.forEach((timer) => clearTimeout(timer));
  recordingToastCleanupTimers.forEach((timer) => clearTimeout(timer));
  recordingToastDismissTimers.clear();
  recordingToastCleanupTimers.clear();
  recordingToastStates.clear();
  recordingToastListeners.clear();
  recordingToastProgress.clear();
  recordingToastAliases.clear();
  recordingToastCeilingHandlers.clear();
}
